import React, { useState, useEffect } from 'react';
import axiosInstance from '../api/axiosInstance';
import { useNavigate } from 'react-router-dom';
import { 
  TextField, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  IconButton, 
  Typography, 
  Alert, 
  CircularProgress,
  Box
} from '@mui/material';
import { Delete, CloudDownload } from '@mui/icons-material';
import { useFavicon } from '../useFavicon';

const LINK_FAVICON_URL = process.env.PUBLIC_URL + "/settings.ico";

const AddMidiPage = () => {
  useFavicon(LINK_FAVICON_URL);
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [midiFiles, setMidiFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');

  // Load MIDI files on component mount
  useEffect(() => {
    loadMidiFiles();
  }, []);

  const loadMidiFiles = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/midi-files');
      setMidiFiles(response.data);
    } catch (error) {
      console.error('Error loading MIDI files:', error);
      showMessage('Failed to load MIDI files', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  const handleAddMidi = async (e) => {
    e.preventDefault();
    
    if (!file) {
      showMessage('Please select a MIDI file', 'error');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploading(true);
      const response = await axiosInstance.post('/midi-files', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setFile(null);
      // Reset the file input
      document.getElementById('midi-file-input').value = '';
      
      // Reload the files list
      await loadMidiFiles();
      
      showMessage(`Successfully uploaded: ${response.data.midiFile.Name}`, 'success');
    } catch (error) {
      console.error('Error adding MIDI file:', error);
      const errorMessage = error.response?.data?.details || error.response?.data?.error || 'Error uploading MIDI file';
      showMessage(errorMessage, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteMidi = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }

    try {
      await axiosInstance.delete(`/midi-files/${id}`);
      await loadMidiFiles(); // Reload the list
      showMessage(`Successfully deleted: ${name}`, 'success');
    } catch (error) {
      console.error('Error deleting MIDI file:', error);
      const errorMessage = error.response?.data?.details || error.response?.data?.error || 'Error deleting MIDI file';
      showMessage(errorMessage, 'error');
    }
  };

  const handleDownloadMidi = async (path, name) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/midi/download/${path}`, { responseType: 'blob' });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${name}.mid`);
      
      // Append to html link element page
      document.body.appendChild(link);
      link.click();
      
      // Clean up and remove the link
      link.parentNode.removeChild(link);
      setLoading(false);
    } catch (error) {
      console.error('Error downloading MIDI file:', error);
      setMessage('Error downloading MIDI file');
      setLoading(false);
    }
  };

  // Navigation handler
  const handleBackToMain = () => {
    navigate('/');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
  };

  const formatDuration = (duration) => {
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h4" gutterBottom>
        MIDI Files Manager
      </Typography>

      {/* Upload Section */}
      <Paper sx={{ padding: 3, marginBottom: 3 }}>
        <Typography variant="h6" gutterBottom>
          Upload New MIDI File
        </Typography>
        <form onSubmit={handleAddMidi}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              id="midi-file-input"
              type="file"
              accept=".mid,.midi"
              onChange={(e) => setFile(e.target.files[0])}
              required
              style={{ minWidth: '200px' }}
            />
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={uploading || !file}
              startIcon={uploading ? <CircularProgress size={20} /> : null}
            >
              {uploading ? 'Uploading...' : 'Upload MIDI'}
            </Button>
          </Box>
        </form>
      </Paper>

      {/* Messages */}
      {message && (
        <Alert severity={messageType} sx={{ marginBottom: 2 }}>
          {message}
        </Alert>
      )}

      {/* MIDI Files Table */}
      <Paper>
        <Typography variant="h6" sx={{ padding: 2 }}>
          MIDI Files ({midiFiles.length})
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Duration</strong></TableCell>
                  <TableCell><strong>Notes</strong></TableCell>
                  <TableCell><strong>Key Signatures</strong></TableCell>
                  <TableCell><strong>Uploaded</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {midiFiles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="textSecondary">
                        No MIDI files uploaded yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  midiFiles.map((midi) => (
                    <TableRow key={midi._id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {midi.Name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {formatDuration(midi.Duration)}
                      </TableCell>
                      <TableCell>
                        {midi.Notes ? midi.Notes.length : 0}
                      </TableCell>
                      <TableCell>
                        {midi.KeySignatures ? midi.KeySignatures.length : 0}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {formatDate(midi.CreatedDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            color="primary"
                            onClick={() => handleDownloadMidi(midi.Path, midi.Name)}
                            title="Download"
                            size="small"
                          >
                            <CloudDownload />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteMidi(midi._id, midi.Name)}
                            title="Delete"
                            size="small"
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      
      {/* Back to Main Page Button */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <Button 
          variant="contained" 
          color="primary"
          onClick={handleBackToMain}
          sx={{ minWidth: 200 }}
        >
          Back to Main Page
        </Button>
      </Box>
    </Box>
  );
};

export default AddMidiPage;